import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TriageLayout from "./TriageLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileCsv, FilePdf, ArrowsClockwise, Warning, ChartBar } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { isSupervisorOrAbove } from "@/lib/permissions";
import { todayIsoDateET } from "@/lib/etTime";

/**
 * Admin Suite — Reports dashboard. Any admin+ can slice triage activity by
 * date range and/or event, then export the sliced dataset as CSV. The
 * page renders four report modules:
 *
 *   1. Overview KPIs         (Total / Transported / RMA / In Triage)
 *   2. Time-based volume     (Daily count table + top-3 busiest days)
 *   3. Per-Event breakdown   (Total + tag mix per event)
 *   4. Per-Center breakdown  (Total + transport per triage centre)
 *
 * Data source: `GET /api/triage/reports/summary?start_date=&end_date=&event_id=`.
 * CSV export reuses `/api/triage/export.csv` with the same filters.
 */
const TAG_COLORS = {
  green:  { label: "Green",   bg: "bg-emerald-500" },
  yellow: { label: "Yellow",  bg: "bg-yellow-400 text-slate-900" },
  orange: { label: "Orange",  bg: "bg-orange-500" },
  red:    { label: "Red",     bg: "bg-red-600" },
  black:  { label: "Black",   bg: "bg-slate-900" },
};

function todayIso() { return todayIsoDateET(); }
function daysAgoIso(n) {
  // Compute "n days ago" in ET so the reports "This week / Last 30 days"
  // presets line up with the ET calendar (matters near midnight).
  const now = new Date();
  // Convert current instant to a rough ET date, then subtract days at
  // UTC-noon to avoid DST edge cases.
  const etIso = todayIsoDateET();
  const [y, m, d] = etIso.split("-").map(Number);
  const base = new Date(Date.UTC(y, m - 1, d, 12));
  base.setUTCDate(base.getUTCDate() - n);
  const yy = base.getUTCFullYear();
  const mm = String(base.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(base.getUTCDate()).padStart(2, "0");
  // `now` var kept for React lint but not used — computation is
  // deterministic off the ET calendar day.
  void now;
  return `${yy}-${mm}-${dd}`;
}

export default function TriageReports() {
  const nav = useNavigate();
  const { user } = useAuth();
  const authorized = isSupervisorOrAbove(user);

  const [preset, setPreset] = useState("month");
  const [startDate, setStartDate] = useState(daysAgoIso(30));
  const [endDate, setEndDate] = useState(todayIso());
  const [eventId, setEventId] = useState("");
  const [events, setEvents] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [csvBusy, setCsvBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    if (!authorized) return;
    api.get("/triage/events", { params: { include_past: true } })
      .then(({ data }) => setEvents(data?.events || []))
      .catch(() => setEvents([]));
  }, [authorized]);

  useEffect(() => {
    if (!authorized) return;
    async function load() {
      setLoading(true);
      try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        if (eventId) params.event_id = eventId;
        const { data } = await api.get("/triage/reports/summary", { params });
        setData(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load reports");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [authorized, startDate, endDate, eventId]);

  function applyPreset(p) {
    setPreset(p);
    if (p === "today") { setStartDate(todayIso()); setEndDate(todayIso()); }
    else if (p === "week") { setStartDate(daysAgoIso(7)); setEndDate(todayIso()); }
    else if (p === "month") { setStartDate(daysAgoIso(30)); setEndDate(todayIso()); }
    else if (p === "quarter") { setStartDate(daysAgoIso(90)); setEndDate(todayIso()); }
    else if (p === "year") { setStartDate(daysAgoIso(365)); setEndDate(todayIso()); }
    else if (p === "all") { setStartDate(""); setEndDate(""); }
  }

  async function downloadCsv() {
    setCsvBusy(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (eventId) params.event_id = eventId;
      // The CSV endpoint doesn't accept start_date/end_date directly (it
      // uses `all_time` toggle), so if any date range is set we request
      // `all_time=true` then trust the report page for filtered totals.
      const csvParams = { ...params, all_time: true };
      const resp = await api.get("/triage/export.csv", { params: csvParams, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      const suffix = eventId ? (events.find((e) => e.id === eventId)?.name || "event") : "all";
      a.href = url;
      a.download = `triage_report_${suffix.replace(/\W+/g, "_")}_${todayIso()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "CSV export failed");
    } finally { setCsvBusy(false); }
  }

  async function downloadPdf() {
    setPdfBusy(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (eventId) params.event_id = eventId;
      const resp = await api.get("/triage/reports/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      // Open in a new tab so the operator can print/save directly.
      const w = window.open(url, "_blank");
      if (!w) {
        // Popup blocked — fall back to a direct download.
        const a = document.createElement("a");
        const suffix = eventId ? (events.find((e) => e.id === eventId)?.name || "event") : "all";
        a.href = url;
        a.download = `triage_report_${suffix.replace(/\W+/g, "_")}_${todayIso()}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF export failed");
    } finally { setPdfBusy(false); }
  }

  const kpis = data?.kpis || {};
  const tagDist = data?.tag_distribution || {};
  const perEvent = data?.per_event || [];
  const perCenter = data?.per_center || [];
  const daily = data?.daily || [];

  const busiestDays = useMemo(() => {
    return [...daily].sort((a, b) => b.count - a.count).slice(0, 3);
  }, [daily]);

  if (!authorized) {
    return (
      <TriageLayout showBack>
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Warning size={40} weight="fill" className="mx-auto text-amber-500 mb-2" />
          <div className="font-display text-xl font-black text-slate-900">Admins Only</div>
          <p className="text-slate-500 text-sm mt-1">The Admin Suite is restricted to Admin and above.</p>
          <Button className="mt-4 rounded-sm" onClick={() => nav("/triage")}>Back to Dashboard</Button>
        </div>
      </TriageLayout>
    );
  }

  return (
    <TriageLayout showBack>
      <div className="max-w-7xl mx-auto px-6 py-6" data-testid="triage-reports">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <ChartBar size={22} weight="bold" className="text-slate-700" />
              <h1 className="font-display text-2xl font-black tracking-tight text-slate-900">Admin Suite · Reports</h1>
            </div>
            <p className="text-slate-500 text-sm mt-1">Overall patient volume, tag distribution, and per-event / per-center breakdowns.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={downloadPdf} disabled={pdfBusy} data-testid="reports-pdf-btn">
              <FilePdf size={14} weight="bold" className="mr-1" /> {pdfBusy ? "Preparing…" : "Print / PDF"}
            </Button>
            <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={downloadCsv} disabled={csvBusy} data-testid="reports-csv-btn">
              <FileCsv size={14} weight="bold" className="mr-1" /> {csvBusy ? "Preparing…" : "Export CSV"}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="surface-grid bg-white p-4 mb-4" data-testid="reports-filters">
          <div className="grid md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="tab-tag">Quick range</Label>
              <div className="flex flex-wrap gap-1 mt-1" data-testid="reports-presets">
                {["today","week","month","quarter","year","all"].map((p) => (
                  <button key={p} onClick={() => applyPreset(p)} data-testid={`reports-preset-${p}`}
                          className={`text-[11px] rounded-sm px-2 py-1 border ${preset === p ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                    {p.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="tab-tag">Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPreset("custom"); }} className="rounded-sm" data-testid="reports-start-date" />
            </div>
            <div>
              <Label className="tab-tag">End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPreset("custom"); }} className="rounded-sm" data-testid="reports-end-date" />
            </div>
            <div>
              <Label className="tab-tag">Event</Label>
              <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="w-full h-10 rounded-sm border border-slate-200 px-2 text-sm bg-white" data-testid="reports-event">
                <option value="">All events</option>
                {events.map((ev) => (<option key={ev.id} value={ev.id}>{ev.name}{ev.event_date ? ` — ${ev.event_date}` : ""}</option>))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-slate-500 py-10">Loading reports…</div>
        ) : (
        <>
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="reports-kpis">
          <StatCard label="Total Patients" value={kpis.total} testid="reports-kpi-total" />
          <StatCard label="Transported" value={kpis.transported} sub={`${kpis.transport_rate || 0}%`} testid="reports-kpi-transported" />
          <StatCard label="Treat & Release" value={kpis.rma} testid="reports-kpi-rma" />
          <StatCard label="Submitted" value={kpis.submitted} testid="reports-kpi-submitted" />
          <StatCard label="In Triage (Open)" value={kpis.in_triage} testid="reports-kpi-in-triage" />
        </div>

        {/* Tag distribution */}
        <div className="surface-grid bg-white p-4 mb-4">
          <div className="tab-tag text-slate-500 mb-2">Triage Tag Distribution</div>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
            {Object.entries(TAG_COLORS).map(([k, meta]) => (
              <div key={k} className={`${meta.bg} rounded-sm py-3 text-center`} data-testid={`reports-tag-${k}`}>
                <div className={`font-display font-black text-3xl ${k === "yellow" ? "text-slate-900" : "text-white"}`}>{tagDist[k] || 0}</div>
                <div className={`text-[11px] font-bold ${k === "yellow" ? "text-slate-900" : "text-white"}`}>{meta.label}</div>
              </div>
            ))}
            <div className="bg-slate-200 rounded-sm py-3 text-center" data-testid="reports-tag-untagged">
              <div className="font-display font-black text-3xl text-slate-800">{tagDist.untagged || 0}</div>
              <div className="text-[11px] font-bold text-slate-800">Untagged</div>
            </div>
          </div>
        </div>

        {/* Time series */}
        <div className="surface-grid bg-white p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="tab-tag text-slate-500">Daily Volume</div>
            <div className="text-[11px] text-slate-500">
              {daily.length} day{daily.length === 1 ? "" : "s"} with activity
              {busiestDays.length > 0 && (
                <> · Busiest: {busiestDays.map((d) => `${d.date} (${d.count})`).join(" · ")}</>
              )}
            </div>
          </div>
          {daily.length === 0 ? (
            <div className="text-center text-slate-400 text-sm py-4">No records in this range.</div>
          ) : (
            <div className="flex items-end gap-1.5 h-32 overflow-x-auto pb-1" data-testid="reports-daily-chart">
              {daily.map((d) => {
                const max = Math.max(...daily.map((x) => x.count));
                // Cap bar height at 96px so the date label always fits below.
                const h = max > 0 ? Math.max(4, (d.count / max) * 96) : 4;
                return (
                  <div key={d.date} className="flex flex-col items-center gap-1 shrink-0 w-8" title={`${d.date}: ${d.count}`}>
                    {/* Bar. Fixed w-6 so a single-day chart doesn't inflate to a giant black block. */}
                    <div className="flex flex-col items-center justify-end h-24 w-6">
                      <div className="text-[9px] font-bold text-slate-500 tabular-nums leading-none mb-0.5">{d.count}</div>
                      <div className="w-full bg-slate-900 rounded-sm" style={{ height: `${h}px` }} />
                    </div>
                    <div className="text-[9px] text-slate-500 whitespace-nowrap">{d.date.slice(5)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Per-Event */}
        <div className="surface-grid bg-white p-4 mb-4">
          <div className="tab-tag text-slate-500 mb-2">Per-Event Breakdown</div>
          {perEvent.length === 0 ? (
            <div className="text-center text-slate-400 text-sm py-4">No events in this range.</div>
          ) : (
            <table className="w-full text-sm" data-testid="reports-per-event-table">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-2 py-1">Event</th>
                  <th className="text-right px-2 py-1">Total</th>
                  <th className="text-right px-2 py-1">Trans.</th>
                  <th className="text-right px-2 py-1">T&amp;R</th>
                  <th className="text-right px-2 py-1 text-emerald-700">Green</th>
                  <th className="text-right px-2 py-1 text-yellow-600">Yellow</th>
                  <th className="text-right px-2 py-1 text-orange-600">Orange</th>
                  <th className="text-right px-2 py-1 text-red-700">Red</th>
                  <th className="text-right px-2 py-1 text-slate-700">Black</th>
                </tr>
              </thead>
              <tbody>
                {perEvent.map((ev) => (
                  <tr key={ev.event_id || "__no_event__"} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-2 py-1 font-medium text-slate-900">{ev.event_name}</td>
                    <td className="px-2 py-1 text-right mono">{ev.total}</td>
                    <td className="px-2 py-1 text-right mono">{ev.transported}</td>
                    <td className="px-2 py-1 text-right mono">{ev.rma}</td>
                    <td className="px-2 py-1 text-right mono">{ev.green}</td>
                    <td className="px-2 py-1 text-right mono">{ev.yellow}</td>
                    <td className="px-2 py-1 text-right mono">{ev.orange}</td>
                    <td className="px-2 py-1 text-right mono">{ev.red}</td>
                    <td className="px-2 py-1 text-right mono">{ev.black}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Per-Center */}
        <div className="surface-grid bg-white p-4">
          <div className="tab-tag text-slate-500 mb-2">Per-Triage-Center Breakdown</div>
          {perCenter.length === 0 ? (
            <div className="text-center text-slate-400 text-sm py-4">No triage centers used in this range.</div>
          ) : (
            <table className="w-full text-sm" data-testid="reports-per-center-table">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-2 py-1">Triage Center</th>
                  <th className="text-right px-2 py-1">Total</th>
                  <th className="text-right px-2 py-1">Transported</th>
                </tr>
              </thead>
              <tbody>
                {perCenter.map((c) => (
                  <tr key={c.center} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-2 py-1 font-medium text-slate-900">{c.center}</td>
                    <td className="px-2 py-1 text-right mono">{c.total}</td>
                    <td className="px-2 py-1 text-right mono">{c.transported}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </>
        )}
      </div>
    </TriageLayout>
  );
}

function StatCard({ label, value, sub, testid }) {
  return (
    <div className="surface-grid bg-white p-4" data-testid={testid}>
      <div className="tab-tag text-slate-500">{label}</div>
      <div className="font-display font-black text-3xl md:text-4xl text-slate-900 tabular-nums mt-1">{value ?? 0}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
